import {
    CreateTextQueueAIParams,
    CreateTextRemixQueueAIParams,
    ExtractFromAudioParams,
    GetInputsTextCreationAIParams,
} from "../types/requestInputs";
import {
    CreateTextQueueAIResponse,
    ExtractFromAudioResponse,
    GlobalContextFormattedResponse,
    GlobalContextResponse,
} from "../types/requestsOutputs";
import { City, DropdownContent, GlobalContextData } from "../types/types";
import { fetchAPI } from "./utils";

export const getGlobalContext = async (token: string, language: string): Promise<GlobalContextFormattedResponse> => {
    const test = await fetchAPI<GlobalContextResponse>("/get-global-context", "GET", undefined, token, language);

    const { categoriesAI, languagesAI, themeAI, targetAudienceAI, cities, guides, filtersAI } = test;

    const categoriesToJson = JSON.parse(categoriesAI.body);
    const languagesAIJson = JSON.parse(languagesAI.body);
    const themeAIJson = JSON.parse(themeAI.body);
    const targetAudienceAIJson = JSON.parse(targetAudienceAI.body);
    const citiesJson = JSON.parse(cities.body);
    const guidesJSON = JSON.parse(guides.body);
    const filtersAIJson = JSON.parse(filtersAI.body);

    return {
        categoriesAI: categoriesToJson,
        languagesAI: languagesAIJson,
        themeAI: themeAIJson,
        targetAudienceAI: targetAudienceAIJson,
        cities: citiesJson,
        guides: guidesJSON,
        filtersAI: filtersAIJson,
    };
};

// Get Inputs for AI Text Creation
export const getInputsTextCreationAI = async ({ token, language = "EN" }: GetInputsTextCreationAIParams) => {
    const response = await getGlobalContext(token, language);
    if (!response) {
        return { error: true, data: null };
    }

    const { categoriesAI, languagesAI, themeAI, targetAudienceAI, cities, guides, filtersAI } = response;

    const categoriesToJson = categoriesAI;
    const languagesAIJson = languagesAI;
    const themeAIJson = themeAI;
    const targetAudienceAIJson = targetAudienceAI;
    const citiesJson = cities;
    const guidesJSON = guides;
    const filtersAIJson = filtersAI;

    const locationCountryOptions = Array.from(new Set(citiesJson.map((city: any) => city.countryId))).map(
        (countryId) => {
            const country = citiesJson.find((city: City) => city.countryId === countryId);
            return { value: country?.countryId || "", label: country?.countryName || "" };
        },
    );

    const locationCityOptions = citiesJson.map((city: any) => ({
        value: city.id,
        label: city.officialName,
        countryId: city.countryId,
    }));

    const themeAIFormatted = themeAIJson.map((theme: any) => ({
        value: theme.id,
        label: theme.name,
    }));

    const targetAudienceAIFormatted = targetAudienceAIJson
        .map((targetAudience: any) => ({
            value: targetAudience.id,
            label: targetAudience.name,
        }))
        .sort((a: DropdownContent, b: DropdownContent) => a.label.localeCompare(b.label));

    const languagesAIFormatted = languagesAIJson.map((language: any) => ({
        value: language.id,
        label: language.name,
        code: language.code,
    }));

    const categoriesAIFormatted = categoriesToJson
        .filter((category: any) => category.isAI)
        .map((category: any) => ({
            value: category.id,
            label: category.name,
        }));

    const categoriesAIFormattedForTrack = categoriesToJson.map((category: any) => ({
        value: category.id,
        label: category.name,
    }));

    const guidesJSONFormatted = guidesJSON.map((guide: any) => ({
        value: guide.id,
        label: `${guide.firstname} ${guide.lastname}`,
    }));

    const filtersAIFormatted = filtersAIJson.map((filter: any) => ({
        value: filter.id,
        label: filter.name,
        categoryAI: filter.categoryAI,
        hasOnlyGardenStories: filter.hasOnlyGardenStories,
        hasOnlyImageScanWaypoints: filter.hasOnlyImageScanWaypoints,
    }));

    const formattedData: GlobalContextData = {
        categoriesAI: categoriesAIFormatted,
        languagesAI: languagesAIFormatted,
        themeAI: themeAIFormatted,
        targetAudienceAI: targetAudienceAIFormatted,
        locationCountryOptions,
        locationCityOptions,
        guides: guidesJSONFormatted,
        categories: categoriesAIFormattedForTrack,
        filtersAI: filtersAIFormatted,
    };

    return { error: false, data: formattedData };
};

export const createTextQueueAI = async ({
    token,
    isFromDb,
    dbSourceId,
    language,
    textObject,
    audioDurationMin,
    audioDurationSec,
    audience,
    hasToneOfVoice,
    toneOfVoice,
    textTitle,
    numberStories,
    requiredContent,
    textType,
    startDate,
    endDate,
    eventType,
    cityId,
    queryFilters,
    restrictToDatabase,
    brandVoiceId,
    audienceProfileId,
}: CreateTextQueueAIParams): Promise<CreateTextQueueAIResponse> => {
    return fetchAPI<CreateTextQueueAIResponse>(
        "/add-one-text-item-queue",
        "POST",
        {
            isFromDb,
            dbSourceId,
            language,
            textObject,
            audioDurationMin,
            audioDurationSec,
            audience,
            hasToneOfVoice,
            toneOfVoice,
            textTitle,
            numberStories,
            requiredContent,
            textType,
            startDate,
            endDate,
            eventType,
            cityId,
            queryFilters,
            restrictToDatabase,
            brandVoiceId: brandVoiceId || null,
            audienceProfileId: audienceProfileId || null,
            isV2: true,
        },
        token,
    );
};

export const createTextRemixQueueAI = async ({
    token,
    language,
    textObject,
    textTitle,
    remixPrompt,
    brandVoiceId,
    audienceProfileId,
}: CreateTextRemixQueueAIParams): Promise<CreateTextQueueAIResponse> => {
    return fetchAPI<CreateTextQueueAIResponse>(
        "/add-one-remix-item-queue",
        "POST",
        {
            language,
            textObject,
            textTitle,
            remixPrompt,
            brandVoiceId: brandVoiceId || null,
            audienceProfileId: audienceProfileId || null,
        },
        token,
    );
};

export const extractFromAudio = async ({
    token,
    audioFile,
    audioAIId,
}: ExtractFromAudioParams): Promise<ExtractFromAudioResponse> => {
    return fetchAPI<ExtractFromAudioResponse>("/convert-audio-to-text", "POST", { audioFile, audioAIId }, token);
};
